import { useState, useEffect } from 'react';
import { api } from '../api';
import { useLive, ago } from '../live';
import { Reading, LiveBadge } from './Garden';
import ModuleHistory from './ModuleHistory';

/**
 * One controller: what is wired to it, what it is sensing, and what you can
 * make it do.
 *
 * The pin map is rendered from the node's reported `hardware`, which is why
 * it was worth fixing the firmware to report its real chip. Offering S3 pins
 * for a classic ESP32 would invite a miswire that the board then refuses at
 * runtime, which is a confusing way to learn you used the wrong pin.
 */

// Pins that exist but cannot be used, per chip. Mirrors the firmware's own
// refusals in modules.c so the UI never offers something the node rejects.
const PIN_RULES = {
  esp32: {
    gpio: Array.from({ length: 40 }, (_, i) => i)
      .filter((p) => !(p >= 6 && p <= 11)),
    // GPIO34-39 have no output driver on this chip.
    inputOnly: [34, 35, 36, 37, 38, 39],
    adc: [0, 1, 2, 3, 4, 5, 6, 7],
    adcGpio: { 0: 36, 1: 37, 2: 38, 3: 39, 4: 32, 5: 33, 6: 34, 7: 35 },
  },
  esp32_s3: {
    gpio: Array.from({ length: 49 }, (_, i) => i)
      .filter((p) => !(p >= 26 && p <= 32) && p !== 19 && p !== 20),
    inputOnly: [],
    adc: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    adcGpio: Object.fromEntries(Array.from({ length: 10 }, (_, i) => [i, i + 1])),
  },
};

function rulesFor(hardware) {
  return PIN_RULES[hardware] || PIN_RULES.esp32_s3;
}

export default function NodeDetail({ nodeId, onBack }) {
  const { data: node, error, updatedAt, refresh } = useLive(() => api.node(nodeId), [nodeId]);
  const [catalog, setCatalog] = useState(null);
  const [adding, setAdding] = useState(false);
  const [historyId, setHistoryId] = useState(null);

  useEffect(() => { api.catalog().then(setCatalog).catch(() => {}); }, []);

  if (!node) {
    return (
      <div className="stack">
        <button className="btn btn-quiet btn-sm" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
          Back to the garden
        </button>
        <p className="faint">{error || 'Loading…'}</p>
      </div>
    );
  }

  const online = node.status === 'online';
  const sensors = (node.modules || []).filter((m) => m.kind === 'sensor');
  const actuators = (node.modules || []).filter((m) => m.kind === 'actuator');

  // The poll above keeps running while history is open, so the module handed
  // to it carries a live `latest` rather than a snapshot from the click.
  const historyModule = sensors.find((m) => m.id === historyId);
  if (historyModule) {
    return (
      <ModuleHistory module={historyModule} node={node}
                     onBack={() => setHistoryId(null)} />
    );
  }

  return (
    <div className="stack">
      <button className="btn btn-quiet btn-sm" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        Back to the garden
      </button>

      <div className="spread">
        <div>
          <h1>{node.name}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {node.hardware} · {node.localIp || 'no address'} · seen {ago(node.lastSeen)}
            {node.rssi ? ` · signal ${node.rssi} dBm` : ''}
          </p>
        </div>
        <div className="row">
          <span className={`pill ${online ? 'pill-online' : 'pill-offline'}`}>
            <span className={`dot ${online ? 'live' : ''}`} />
            {online ? 'Connected' : 'Asleep'}
          </span>
          <LiveBadge updatedAt={updatedAt} error={error} onRefresh={refresh} />
        </div>
      </div>

      {!online && (
        <div className="notice notice-info">
          This controller has gone quiet. Anything you ask for now would wait
          until it wakes, so controls stay disabled until it is back.
        </div>
      )}

      {sensors.length > 0 && (
        <div className="card">
          <h3>Sensing</h3>
          <p className="muted" style={{ margin: '4px 0 var(--s4)' }}>
            Now, and as far back as the record goes. Open any one of them.
          </p>
          <div className="row" style={{ gap: 'var(--s5)' }}>
            {sensors.map((m) => (
              <button key={m.id} type="button" className="reading-link"
                      onClick={() => setHistoryId(m.id)}>
                <Reading module={m} />
                <span className="hint">History →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {actuators.length > 0 && (
        <div className="stack">
          <h3>Controls</h3>
          <div className="grid">
            {actuators.map((m) => (
              <ActuatorCard key={m.id} module={m} online={online} onDone={refresh} />
            ))}
          </div>
        </div>
      )}

      <PinMap node={node} />

      {adding ? (
        <AddModule node={node} catalog={catalog}
                   onCancel={() => setAdding(false)}
                   onAdded={() => { setAdding(false); refresh(); }} />
      ) : (
        <button className="btn btn-primary" onClick={() => setAdding(true)}
                style={{ alignSelf: 'flex-start' }}>
          Wire something up
        </button>
      )}
    </div>
  );
}

/**
 * One actuator, with a bounded run control.
 *
 * Duration is always explicit. There is deliberately no plain on/off toggle:
 * a latched pump on a controller that then loses its network is a flooded
 * floor, and the whole system is built so that cannot be expressed.
 */
function ActuatorCard({ module, online, onDone }) {
  const maxRun = module.limits?.max_run_s || 60;
  const [seconds, setSeconds] = useState(Math.min(10, maxRun));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const isDosing = module.moduleType === 'dosing_pump';
  const blocked = isDosing && !module.calibrated;

  async function run() {
    setBusy(true); setError(null); setMessage(null);
    try {
      const result = await api.runModule(module.id, Number(seconds));
      setMessage(`Running for ${result.seconds}s.`);
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    setBusy(true); setError(null);
    try { await api.stopModule(module.id); setMessage('Stopping.'); onDone(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="card stack" style={{ gap: 'var(--s4)' }}>
      <div className="spread">
        <div>
          <h3>{module.name || module.label}</h3>
          <span className="faint">{module.port}{module.role ? ` · ${module.role}` : ''}</span>
        </div>
        {module.lastActuatedAt && (
          <span className="faint">ran {ago(module.lastActuatedAt)}</span>
        )}
      </div>

      {blocked && (
        <div className="notice notice-info">
          This pump has no millilitres per second calibration, so a dose
          cannot be turned into a run time. Calibrate it before it will run.
        </div>
      )}

      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 auto' }}>
          <label htmlFor={`sec-${module.id}`}>
            Run for {seconds}s
            <span className="faint" style={{ fontWeight: 400 }}> (up to {maxRun}s)</span>
          </label>
          <input id={`sec-${module.id}`} type="range" min={1} max={maxRun}
                 value={seconds} onChange={(e) => setSeconds(e.target.value)}
                 style={{ padding: 0, background: 'transparent', border: 'none' }} />
        </div>
        <button className="btn btn-water" onClick={run}
                disabled={busy || !online || blocked}>
          {busy ? 'Sending…' : 'Run'}
        </button>
        <button className="btn btn-quiet btn-sm" onClick={stop} disabled={busy || !online}>
          Stop
        </button>
      </div>

      {error && <div className="notice notice-error">{error}</div>}
      {message && !error && <div className="notice notice-good">{message}</div>}
    </div>
  );
}

/** Which pins are taken, which are free, which this chip cannot output on. */
function PinMap({ node }) {
  const rules = rulesFor(node.hardware);
  const used = new Map((node.modules || []).map((m) => [m.port, m]));

  return (
    <div className="card stack">
      <div>
        <h3>Pins</h3>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          What is wired where on this {node.hardware}.
        </p>
      </div>

      <div>
        <span className="label" style={{ display: 'block', marginBottom: 'var(--s2)' }}>
          Analog in
        </span>
        <div className="ports">
          {rules.adc.map((channel) => {
            const port = `adc${channel}`;
            const module = used.get(port);
            return (
              <div key={port} className={`port ${module ? 'used' : ''}`}
                   title={module ? module.label : `GPIO ${rules.adcGpio[channel]}`}>
                {port}
                <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                  {module ? module.label.split(' ')[0] : `g${rules.adcGpio[channel]}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <span className="label" style={{ display: 'block', marginBottom: 'var(--s2)' }}>
          Digital
        </span>
        <div className="ports">
          {rules.gpio.map((pin) => {
            const port = `gpio${pin}`;
            const module = used.get(port);
            const inputOnly = rules.inputOnly.includes(pin);
            return (
              <div key={port}
                   className={`port ${module ? 'used' : ''} ${inputOnly && !module ? 'unusable' : ''}`}
                   title={module ? module.label
                        : inputOnly ? 'Input only on this chip. Cannot drive an actuator.'
                        : 'Free'}>
                {port}
                {module && (
                  <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                    {module.label.split(' ')[0]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {rules.inputOnly.length > 0 && (
          <p className="faint" style={{ marginTop: 'var(--s3)' }}>
            Struck through pins can read but cannot drive anything. On this
            chip they have no output driver at all, so a pump wired there
            would look configured and never run.
          </p>
        )}
      </div>
    </div>
  );
}

/** Attach a module: pick what it is, then where it is plugged in. */
function AddModule({ node, catalog, onCancel, onAdded }) {
  const [moduleType, setModuleType] = useState('');
  const [port, setPort] = useState('');
  const [role, setRole] = useState('');
  const [name, setName] = useState('');
  const [inverted, setInverted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const rules = rulesFor(node.hardware);
  const taken = new Set((node.modules || []).map((m) => m.port));
  const spec = catalog?.modules.find((m) => m.type === moduleType);
  const isSensor = spec?.kind === 'sensor';
  const wantsAnalog = spec?.drive?.includes('adc');

  // Offer only ports this chip can actually use for this kind of module.
  const ports = [];
  if (wantsAnalog || isSensor) {
    rules.adc.forEach((c) => { if (!taken.has(`adc${c}`)) ports.push(`adc${c}`); });
  }
  rules.gpio.forEach((pin) => {
    const p = `gpio${pin}`;
    if (taken.has(p)) return;
    if (!isSensor && rules.inputOnly.includes(pin)) return;  // cannot drive
    ports.push(p);
  });

  async function submit(event) {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.addModule(node.nodeId, {
        module_type: moduleType,
        port,
        role: role || null,
        name: name || null,
        inverted,
        drive: port.startsWith('adc') ? 'adc' : (spec?.drive?.includes('pwm') ? 'pwm' : 'gpio'),
      });
      onAdded();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  if (!catalog) return <p className="faint">Loading the parts catalog…</p>;

  const sensors = catalog.modules.filter((m) => m.kind === 'sensor');
  const actuators = catalog.modules.filter((m) => m.kind === 'actuator');

  return (
    <form className="card stack" onSubmit={submit}>
      <h3>Wire something up</h3>

      <div>
        <label htmlFor="mtype">What is it</label>
        <select id="mtype" required value={moduleType}
                onChange={(e) => { setModuleType(e.target.value); setPort(''); setRole(''); }}>
          <option value="">Choose a part…</option>
          <optgroup label="Sensors">
            {sensors.map((m) => <option key={m.type} value={m.type}>{m.label}</option>)}
          </optgroup>
          <optgroup label="Actuators">
            {actuators.map((m) => <option key={m.type} value={m.type}>{m.label}</option>)}
          </optgroup>
        </select>
        {spec?.notes && <p className="faint" style={{ marginTop: 'var(--s2)' }}>{spec.notes}</p>}
      </div>

      {spec?.roles && (
        <div>
          <label htmlFor="mrole">
            {moduleType === 'dosing_pump' ? 'What is in the tube' : 'Which channel'}
          </label>
          <select id="mrole" required value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">Choose…</option>
            {spec.roles.map((r) => (
              <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      )}

      {moduleType && (
        <div>
          <label htmlFor="mport">Plugged into</label>
          <select id="mport" required value={port} onChange={(e) => setPort(e.target.value)}>
            <option value="">Choose a pin…</option>
            {ports.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="mname">Call it</label>
        <input id="mname" value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Optional" />
      </div>

      {!isSensor && moduleType && (
        <label className="row" style={{ gap: 'var(--s2)', cursor: 'pointer' }}>
          <input type="checkbox" checked={inverted}
                 onChange={(e) => setInverted(e.target.checked)}
                 style={{ width: 'auto' }} />
          <span>
            Active low
            <span className="faint"> — the board switches ON when the pin is
            LOW. If the output is on while idle and goes off when you press
            Run, tick this.</span>
          </span>
        </label>
      )}

      {error && <div className="notice notice-error">{error}</div>}

      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={busy || !moduleType || !port}>
          {busy ? 'Adding…' : 'Add it'}
        </button>
        <button className="btn btn-quiet" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
